import { zodResolver } from '@hookform/resolvers/zod';
import Save from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { FieldErrors, FormProvider, Path, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FloatingSaveButton } from '@/components/floating-save-button';

import AuthGuard from '@/components/auth-guard';
import { BasicInfoSection } from '@/components/codex-form/basic-info';
import { CustomFieldsSection } from '@/components/codex-form/custom-fields-section';
import { ImageManager } from '@/components/codex-form/image-manager';
import { FeatureGuard } from '@/components/feature-guard';
import { Page } from '@/components/page';
import {
  CodexFormSchema,
  CodexFormValues,
  useCodexAccessLayer
} from '@/features/codex';
import { useImageProcessor } from '@/features/shared/hooks/use-image-processor';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { Codex as DomainCodex } from '@/lib/types';
import { splitBySchemaShape } from '@/lib/utils/split-by-schema';

import { z } from 'zod';

export const Route = createFileRoute('/codex/new')({
  component: NewCodexEntryPage,
  validateSearch: (search: Record<string, unknown>) => {
    return z.object({
      type: z.string().optional(),
    }).parse(search);
  },
});

function NewCodexEntryForm() {
  const { type } = Route.useSearch();
  const { novel, getNewId } = useNovelRepository();
  const novelId = novel?.id;
  const { toast } = useToast();
  const resolveResponsive = useResponsiveValue();
  const { t } = useTranslation();

  const { processImages, isProcessing: isProcessingImages } = useImageProcessor();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  if (!novel.id) {
    navigate({ to: '/codex' });
    return null;
  }

  const categories = useMemo(() => {
    const categoryMap = novel?.codexCategories || {};
    return Object.keys(categoryMap);
  }, [novel]);

  const methods = useForm<CodexFormValues>({
    resolver: zodResolver(CodexFormSchema),
    defaultValues: {
      name: '',
      type: type || categories[0] || '',
      shortDescription: '',
      content: '',
      visualDescription: '',
      images: [],
      primaryImageId: null,
      pendingFiles: [],
      audit: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    },
  });

  const entryId = useMemo(() => getNewId(), [getNewId]);

  const {
    createEntry: createEntryLayer,
    entry: combinedData,
    sections: codexSettings,
  } = useCodexAccessLayer(entryId, methods.watch('type'));

  // Sync images and primary index from background updates (e.g. AI generation)
  // This is crucial for New entries because AI generation will create a draft document in Firestore
  useEffect(() => {
    if (combinedData?.images && JSON.stringify(combinedData.images) !== JSON.stringify(methods.getValues('images'))) {
      methods.setValue('images', combinedData.images as CodexFormValues['images'], { shouldDirty: true });
    }
    if (combinedData?.primaryImageId !== undefined && combinedData.primaryImageId !== methods.getValues('primaryImageId')) {
      methods.setValue('primaryImageId', combinedData.primaryImageId, { shouldDirty: true });
    }
  }, [combinedData?.images, combinedData?.primaryImageId, methods]);

  const onSubmit = async (values: CodexFormValues) => {
    if (!novelId) return;

    setIsSaving(true);
    try {
      const { pendingFiles, ...entryData } = values;
      const { defined: coreData, details } = splitBySchemaShape(CodexFormSchema, entryData);

      let imagesUpdatedArray = [...(coreData.images || [])];

      if (pendingFiles && pendingFiles.length > 0) {
        const processedResults = await processImages(
          pendingFiles,
          novelId,
          `codex/${entryId}`,
          novel.ownerId
        );
        imagesUpdatedArray = [...imagesUpdatedArray, ...processedResults];
      }

      const primaryImageId = coreData.primaryImageId ?? imagesUpdatedArray[0]?.id ?? null;
      const mainImage = imagesUpdatedArray.find(image => image.id === primaryImageId);
      const mainImageUrl = mainImage?.profile?.url || mainImage?.original?.url || '';
      const mainImagePath = mainImage?.profile?.path || mainImage?.original?.path || '';

      await createEntryLayer(entryId, {
        ...coreData,
        id: entryId,
        details,
        images: imagesUpdatedArray,
        primaryImageId,
        mainImageUrl,
        mainImagePath,
      });

      toast({
        title: t('common.success'),
        description: t('entity_form.codex.create_success_desc', { name: values.name }),
      });

      navigate({ to: '/codex' });
    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: t('entity_form.common.save_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onInvalid = (errors: FieldErrors<CodexFormValues>) => {
    const errorFields = Object.keys(errors).map(key => {
      return key.charAt(0).toUpperCase() + key.slice(1);
    });

    toast({
      title: t('entity_form.common.validation_failed_title'),
      description: t('entity_form.common.validation_failed_desc', { fields: errorFields.join(', ') }),
      variant: 'destructive',
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
        <Page
          title={t('entity_list.codex.new')}
          description={t('entity_list.codex.description')}
          breadcrumbs={[
            { label: t('entity_list.codex.title'), href: '/codex' },
            { label: t('entity_list.codex.new') }
          ]}
          layoutMode="scrolling"
          actions={
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: '1rem' }} />}
              sx={{ px: 4, borderRadius: 2 }}
            >
              {t('common.save')}
            </Button>
          }
        >

          <Grid container spacing={4}>
            <Grid size={resolveResponsive({ xs: 12, lg: 8 })}>
              <Stack spacing={4}>
                <BasicInfoSection isEdit={false} />

                <section id="custom-fields">
                  {novelId && <CustomFieldsSection novelId={novelId} sections={codexSettings} />}
                </section>
              </Stack>
            </Grid>

            <Grid size={resolveResponsive({ xs: 12, lg: 4 })}>
              <Box component="aside" sx={{ position: { lg: 'sticky' }, top: 24 }}>
                {novelId && (
                  <ImageManager
                    onUpdate={(updates) => {
                      (Object.keys(updates) as Array<keyof typeof updates>).forEach((key) => {
                        const value = updates[key];
                        if (value !== undefined) {
                          methods.setValue(key as Path<CodexFormValues>, value, { shouldDirty: true, shouldTouch: true });
                        }
                      });
                    }}
                    isProcessing={isProcessingImages}
                    entry={{ ...methods.getValues(), id: entryId } as DomainCodex}
                    pendingFiles={(methods.watch('pendingFiles') as File[]) || []}
                    visualDescription={methods.watch('visualDescription')}
                    onVisualDescriptionChange={(value) => methods.setValue('visualDescription', value, { shouldDirty: true })}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Page>
        <FloatingSaveButton
          isSaving={isSaving}
          disabled={isSaving}
          label={t('common.save')}
        />
      </form>
    </FormProvider>
  );
}

function NewCodexEntryPage() {
  const { t } = useTranslation();
  return (
    <FeatureGuard featureKey="should_display_codex_page">
      <AuthGuard>
        <Suspense fallback={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 3 }}>
            <CircularProgress color="primary" thickness={2} size={48} />
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.2em' }}>
              {t('entity_form.common.initializing')}
            </Typography>
          </Box>
        }>
          <NewCodexEntryForm />
        </Suspense>
      </AuthGuard>
    </FeatureGuard>
  );
}
